import { useId, useState, type ReactNode } from 'react';
import { useSession } from '../session';
import { Sheet } from '../ui/Sheet';
import { Button, iconButton } from '../ui/Button';
import { useOnline } from '../ui/useConnection';
import { IconLogout } from '../icons';
import {
  CLOSED,
  decideSignOut,
  signOutAndReset,
  signOutGuardStep,
  type SignOutGuardEvent,
  type SignOutSheetState,
} from '../../lib/sign-out';
import { accountRef, type AccountRef } from '../../lib/account';

/**
 * Signing out with work still on the device (MAP 225b).
 *
 * Signing out KEEPS this person's unsent changes, photos and refused writes on the device for
 * their next sign-in here. The warning only makes sure they know ("2 changes and 1 photo
 * haven't been sent yet", "1 thing needs you"), with Stay signed in as the default (also Esc
 * and the overlay). With nothing unsent it signs out at once.
 *
 * Offline, signing out cannot happen at all (the cookie survives a failed call, so the next
 * start would still be them), so the control says so instead of pretending.
 */

export const OFFLINE_REASON = 'Signing out needs a connection.';

/**
 * The guarded sign-out, for any control that signs out: `request()` on press, and `dialog`
 * rendered beside the control. The counts are read when it is pressed, not before. Offline,
 * the control is described by OFFLINE_REASON, rendered with the id `reasonId`.
 */
export function useSignOutGuard(
  account: AccountRef | null,
  to = '/',
): {
  request: () => void;
  offline: boolean;
  asking: boolean;
  reasonId: string;
  dialog: ReactNode;
} {
  const online = useOnline();
  const reasonId = useId();
  const [sheet, setSheet] = useState<SignOutSheetState>(CLOSED);
  const [asking, setAsking] = useState(false);

  const step = (event: SignOutGuardEvent, from: SignOutSheetState = sheet) => {
    const next = signOutGuardStep(from, event);
    setSheet(next.state);
    if (next.signOut) void signOutAndReset({ to });
  };

  const request = () => {
    if (asking) return;
    setAsking(true);
    void decideSignOut(account, online)
      .then((decision) => step({ type: 'decided', decision }))
      .finally(() => setAsking(false));
  };

  return {
    request,
    offline: !online,
    asking,
    reasonId,
    dialog: (
      <SignOutSheet
        sheet={sheet}
        onStay={() => step({ type: 'stay' })}
        onSignOutAnyway={() => step({ type: 'sign-out-anyway' })}
      />
    ),
  };
}

/** The offline explanation, or the warning, in the kit's Sheet. */
export function SignOutSheet({
  sheet,
  onStay,
  onSignOutAnyway,
}: {
  sheet: SignOutSheetState;
  onStay: () => void;
  onSignOutAnyway: () => void;
}) {
  const shown = sheet.shown;
  if (shown?.kind === 'offline') {
    return (
      <Sheet
        open={sheet.open}
        onClose={onStay}
        title="Signing out needs a connection"
        footer={
          <Button autoFocus className="w-full" onClick={onStay}>
            OK
          </Button>
        }
      >
        <p className="text-sm">Everything you did is saved on this device.</p>
      </Sheet>
    );
  }
  if (shown?.kind === 'warn') {
    return (
      <Sheet
        open={sheet.open}
        onClose={onStay}
        title={shown.warning.title}
        footer={
          <div className="flex flex-col gap-2">
            <Button autoFocus className="w-full" onClick={onStay}>
              Stay signed in
            </Button>
            <Button variant="ghost" className="w-full" onClick={onSignOutAnyway}>
              Sign out anyway
            </Button>
          </div>
        }
      >
        {shown.warning.needsYou ? (
          <p className="text-sm font-semibold">{shown.warning.needsYou}</p>
        ) : null}
        <p className="mt-1 text-sm text-muted-foreground">{shown.warning.body}</p>
      </Sheet>
    );
  }
  return null;
}

/**
 * The shells' sign-out icon button (TechShell, OfficeShell): the kit's icon button, guarded.
 * Offline it is dimmed and described by the reason, and pressing it says why: it stays
 * pressable (not `aria-disabled`, which the kit makes unclickable), so a thumb gets the
 * explanation too.
 */
export function SignOutButton({ iconSize }: { iconSize: number }) {
  const session = useSession();
  const guard = useSignOutGuard(accountRef(session));
  return (
    <>
      <button
        type="button"
        onClick={guard.request}
        aria-label="Sign out"
        aria-describedby={guard.offline ? guard.reasonId : undefined}
        aria-busy={guard.asking || undefined}
        title={guard.offline ? OFFLINE_REASON : 'Sign out'}
        className={`${iconButton} ${guard.offline ? 'opacity-40' : ''}`}
      >
        <IconLogout size={iconSize} />
      </button>
      {guard.offline ? (
        <span id={guard.reasonId} className="sr-only">
          {OFFLINE_REASON}
        </span>
      ) : null}
      {guard.dialog}
    </>
  );
}
